import type { ServerError } from '../models';

export const ERROR_MSG_INVALID_CODE = 'Retype your code or request a new one.';

// Friendly copy for known 403 reasons, keyed by the backend error `code`. Used only when the
// server sends a code but no usable details/message (e.g. message === code), so we surface a
// specific reason instead of the generic forbidden fallback.
const FORBIDDEN_REASON_BY_CODE: Record<string, string> = {
    promotion_is_not_available_in_your_region: "This promotion isn't available in your region.",
    user_not_kyc_verified: 'Please finish verifying your identity to continue.',
    employment_trading_blocked: 'Trading is paused while we review your employment details.',
    trading_blocked: 'Trading is currently restricted on your account.',
    user_deactivated: 'Your account is deactivated. Reactivate it to continue.',
};

const getErrorMessage = (srvErr: ServerError): string => {
    const apiError = srvErr.error;
    const details = apiError?.details;
    if (details && details.trim() !== '') {
        return details;
    }
    if (
        apiError?.message &&
        apiError?.message !== '' &&
        apiError?.message !== 'internal_server_error' &&
        apiError?.message !== apiError?.code
    ) {
        return apiError?.message;
    }
    // All the messages below are intentionally vague, because these are the fallback messages
    // They will only be used if we don't have specific messages for the error codes
    if (srvErr.maintenance) {
        return 'Kalshi is under maintenance.';
    }
    if (srvErr.unavailable) {
        return 'Service unavailable, please try again later.';
    }
    if (srvErr.notFound) {
        return 'The information you are trying to access does not exist.';
    }
    if (srvErr.invalid) {
        return 'Invalid input(s), please review the information provided.';
    }
    if (srvErr.forbidden) {
        const code = apiError?.code;
        if (code && FORBIDDEN_REASON_BY_CODE[code]) {
            return FORBIDDEN_REASON_BY_CODE[code];
        }
        return "You don't have permission to perform this operation.";
    }
    if (srvErr.conflict) {
        // Conflict is used when you violate one of the business rule like submitting orders for closed markets and so on
        return "We couldn't accept this operation.";
    }
    if (srvErr.tooManyRequests) {
        return 'This user issued too many requests.';
    }
    // srvErr.internal or anything not specified
    return 'Something went wrong, please try again later.';
};

export { getErrorMessage };
